<div class="dashboard-container">
	<app-spinner [loading]="isLoading">

	@if (loadError) {
		<div class="card dashboard-error" role="alert">
			<lucide-icon name="TriangleAlert" [size]="32"></lucide-icon>
			<p>{{ 'common.states.loadError' | transloco }}</p>
			<app-button
				[label]="'common.actions.retry' | transloco"
				variant="secondary"
				size="small"
				(onClick)="retry()"
			></app-button>
		</div>
	} @else {

	<!-- KPI Cards -->
	<div class="kpi-grid">
		<div class="kpi-card">
			<div class="kpi-header">
				<span class="kpi-label">{{ 'dashboard.kpi.totalRevenue' | transloco }}</span>
				<div class="kpi-icon">
					<lucide-icon name="DollarSign"></lucide-icon>
				</div>
			</div>
			<div class="kpi-value">{{ totalRevenue | appCurrency }}</div>
		</div>

		<div class="kpi-card">
			<div class="kpi-header">
				<span class="kpi-label">{{ 'dashboard.kpi.totalOrders' | transloco }}</span>
				<div class="kpi-icon">
					<lucide-icon name="ShoppingCart"></lucide-icon>
				</div>
			</div>
			<div class="kpi-value">{{ totalOrders | appNumber }}</div>
		</div>

		<div class="kpi-card">
			<div class="kpi-header">
				<span class="kpi-label">{{ 'dashboard.kpi.registeredClients' | transloco }}</span>
				<div class="kpi-icon">
					<lucide-icon name="Users"></lucide-icon>
				</div>
			</div>
			<div class="kpi-value">{{ totalUsers | appNumber }}</div>
		</div>

		<div class="kpi-card">
			<div class="kpi-header">
				<span class="kpi-label">{{ 'dashboard.kpi.avgTicket' | transloco }}</span>
				<div class="kpi-icon">
					<lucide-icon name="CreditCard"></lucide-icon>
				</div>
			</div>
			<div class="kpi-value">{{ avgTicket | appCurrency }}</div>
		</div>
	</div>

	<!-- Revenue Breakdown (Beltrashow only) -->
	@if (showRevenueBreakdown && revenueBreakdown) {
		<div class="card breakdown-card">
			<div class="card-header">
				<h3>{{ 'dashboard.revenueBreakdown.title' | transloco }}</h3>
				<span class="card-badge">{{ brandName }}</span>
			</div>
			<div class="breakdown-grid">
				<div class="breakdown-item owner">
					<span class="breakdown-label">{{ 'dashboard.revenueBreakdown.ownerRevenue' | transloco }}</span>
					<span class="breakdown-value">{{ revenueBreakdown.ownerRevenue | appCurrency }}</span>
				</div>
				<div class="breakdown-item clients">
					<span class="breakdown-label">{{ 'dashboard.revenueBreakdown.clientsRevenue' | transloco }}</span>
					<span class="breakdown-value">{{ revenueBreakdown.clientsRevenue | appCurrency }}</span>
				</div>
				<div class="breakdown-item commission">
					<span class="breakdown-label">{{ 'dashboard.revenueBreakdown.partnerCommission' | transloco }}</span>
					<span class="breakdown-value">{{ revenueBreakdown.partnerCommission | appCurrency }}</span>
				</div>
				<div class="breakdown-item samu">
					<span class="breakdown-label">{{ 'dashboard.revenueBreakdown.samuRevenue' | transloco }}</span>
					<span class="breakdown-value">{{ revenueBreakdown.samuRevenue | appCurrency }}</span>
				</div>
			</div>
		</div>
	}

	<!-- Charts Row -->
	<div class="charts-row">
		<!-- Revenue Bar Chart -->
		<div class="card chart-card">
			<div class="card-header">
				<h3>{{ 'dashboard.charts.monthlyRevenue' | transloco }}</h3>
				<span class="card-badge">
					@if (isYearPeriod()) {
						{{ dashboardFilters.selectedPeriod() }}
					} @else {
						{{ 'dashboard.charts.last6Months' | transloco }}
					}
				</span>
			</div>
			<div class="chart-container">
				<div class="bar-chart">
					@for (item of monthlyRevenue; track item.month) {
						<div class="bar-wrapper">
							<div class="bar-value">{{ item.revenue | appCurrency: '1.0-0' }}</div>
							<div class="bar-track">
								<div class="bar" [style.height.%]="getBarHeight(item.revenue)"></div>
							</div>
							<div class="bar-label">{{ item.month }}</div>
						</div>
					}
				</div>
			</div>
		</div>

		<!-- Top Platforms -->
		<div class="card platforms-card">
			<div class="card-header">
				<h3>{{ 'dashboard.charts.topPlatforms' | transloco }}</h3>
				<span class="card-badge">{{ 'dashboard.charts.byOrders' | transloco }}</span>
			</div>
			<div class="platforms-list">
				@for (platform of topPlatforms; track platform.name) {
					<div class="platform-item">
						<div class="platform-header">
							<span class="platform-name">{{ platform.name }}</span>
							<span class="platform-pct">{{ platform.percentage }}%</span>
						</div>
						<div class="platform-bar-track">
							<div class="platform-bar-fill" [style.width.%]="platform.percentage"></div>
						</div>
						<span class="platform-orders">{{ platform.orders | appNumber }} {{ (platform.orders === 1 ? 'dashboard.charts.order' : 'dashboard.charts.orders') | transloco }}</span>
					</div>
				} @empty {
					<p class="card-empty">{{ 'dashboard.charts.noPlatformData' | transloco }}</p>
				}
			</div>
		</div>
	</div>

	<!-- Payment Methods -->
	<div class="card payment-methods-card">
		<div class="card-header">
			<h3>{{ 'dashboard.paymentMethods.title' | transloco }}</h3>
			<span class="card-badge">{{ 'dashboard.paymentMethods.byOrdersInPeriod' | transloco }}</span>
		</div>

		<div class="payment-methods-grid">
			@for (stat of paymentMethods; track stat.method) {
				<div class="pm-item">
					<div class="pm-header">
						<span class="pm-name">{{ getMethodLabel(stat.method) }}</span>
						<span class="pm-orders">{{ stat.orders | appNumber }} {{ (stat.orders === 1 ? 'dashboard.charts.order' : 'dashboard.charts.orders') | transloco }}</span>
					</div>

					<div class="pm-bar-track">
						<div
							class="pm-bar-completed"
							[style.width.%]="getCompletedWidth(stat)"
						></div>
						<div
							class="pm-bar-failed"
							[style.width.%]="100 - getCompletedWidth(stat)"
						></div>
					</div>

					<div class="pm-stats">
						<span class="pm-stat">
							<span class="pm-stat-dot completed"></span>
							{{ stat.completed | appNumber }} {{ 'dashboard.paymentMethods.completed' | transloco }}
						</span>
						<span class="pm-stat">
							<span class="pm-stat-dot failed"></span>
							{{ stat.notCompleted | appNumber }} {{ 'dashboard.paymentMethods.notCompleted' | transloco }}
						</span>
					</div>

					<div class="pm-financials">
						<div class="pm-financial">
							<span class="pm-financial-label">{{ 'dashboard.paymentMethods.revenue' | transloco }}</span>
							<span class="pm-financial-value">{{ stat.revenue | appCurrency }}</span>
						</div>
						<div class="pm-financial">
							<span class="pm-financial-label">{{ 'dashboard.paymentMethods.avgTicket' | transloco }}</span>
							<span class="pm-financial-value">{{ stat.avgTicket | appCurrency }}</span>
						</div>
						<div class="pm-financial">
							<span class="pm-financial-label">{{ 'dashboard.paymentMethods.percentTotal' | transloco }}</span>
							<span class="pm-financial-value">{{ stat.percentage }}%</span>
						</div>
					</div>
				</div>
			} @empty {
				<p class="card-empty">{{ 'dashboard.paymentMethods.empty' | transloco }}</p>
			}
		</div>
	</div>

	}

	</app-spinner>

</div>
